import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { EXAM_PASS_SCORE } from '@proref/schemas';
import { Icon } from '../../components/shared/Icons';
import { PageHeader } from '../../components/shared/PageHeader';
import { Skeleton } from '../../components/shared/Skeleton';
import { ExamResultItemCard } from '../../components/exams/ExamResultItemCard';
import { apiErrorMessage } from '../../lib/api';
import { useExamResultDetailQuery } from '../../hooks/use-exams';

// One candidate's sitting, opened from a row of the cohort table. The admin's counterpart to the
// referee's own results page: same graded cards, plus who took it and the proctoring tallies that
// only an admin ever sees.

const PASS_PERCENT = EXAM_PASS_SCORE * 100;

function pct(score: number): number {
  return Math.round(score * 100);
}

function hhmmss(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// A proctoring tally reads as plain text at zero and only earns a warn tint once it fired — a
// row of red zeroes would make every clean sitting look suspect.
function Flag({ count, label }: { count: number; label: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2">
      <div className={'font-mono text-[15px] font-semibold ' + (count > 0 ? 'text-warn' : 'text-ink')}>{count}</div>
      <div className="text-[11px] text-muted">{label}</div>
    </div>
  );
}

export function ExamResultDetailPage() {
  const { t } = useTranslation();
  const { id, attemptId } = useParams<{ id: string; attemptId: string }>();
  const { data, isLoading, isError, error } = useExamResultDetailQuery(id, attemptId);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('admin.exams.resultDetail.couldNotLoad'))}
      </div>
    );
  }

  const { candidate, proctoring, results, passed, duration_sec } = data;

  return (
    <div>
      <div className="mb-2">
        <Link
          to={`/admin/exams/${id}/results`}
          className="inline-flex items-center gap-1 text-[12px] text-muted hover:text-ink"
        >
          <Icon.chevronLeft className="h-3.5 w-3.5" /> {t('admin.exams.resultDetail.allResults')}
        </Link>
      </div>

      <PageHeader
        title={candidate.full_name}
        sub={
          candidate.licence_level
            ? t('admin.exams.resultDetail.licenceAndOneSitting', { licence: candidate.licence_level })
            : t('admin.exams.resultDetail.oneSitting')
        }
      />

      {/* Score line. The verdict comes from the same shared threshold the cohort table and the
          candidate's own page use, so the three can never disagree. */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
        <div className="rounded-lg border border-line bg-surface px-3 py-2">
          <div className={'font-mono text-[15px] font-semibold ' + (passed ? 'text-accent' : 'text-danger')}>
            {pct(results.score)}%
          </div>
          <div className="text-[11px] text-muted">
            {passed
              ? t('admin.exams.resultDetail.passed', { percent: PASS_PERCENT })
              : t('admin.exams.resultDetail.failed', { percent: PASS_PERCENT })}
          </div>
        </div>
        <div className="rounded-lg border border-line bg-surface px-3 py-2">
          <div className="font-mono text-[15px] font-semibold text-ink">
            {results.earned_points}/{results.max_points}
          </div>
          <div className="text-[11px] text-muted">
            {t('admin.exams.resultDetail.pointsLabel', { correct: results.correct, total: results.total })}
          </div>
        </div>
        <div className="rounded-lg border border-line bg-surface px-3 py-2">
          <div className="font-mono text-[15px] font-semibold text-ink">{hhmmss(duration_sec)}</div>
          <div className="text-[11px] text-muted">{t('admin.exams.resultDetail.duration')}</div>
        </div>
        <Flag count={proctoring.focus_lost_count} label={t('admin.exams.results.flagLeftExam')} />
        <Flag count={proctoring.fullscreen_exit_count} label={t('admin.exams.results.flagLeftFullscreen')} />
      </div>

      <div className="space-y-3">
        {results.items.map((item, i) => (
          <ExamResultItemCard key={item.exam_question_id} item={item} index={i} voice="candidate" />
        ))}
      </div>
    </div>
  );
}
